import { DataAction, Payload, StateRepository } from '@angular-ru/ngxs/decorators';
import { Injectable } from '@angular/core';
import { Selector, SelectorOptions, State, Store } from '@ngxs/store';
import { produce } from 'immer';
import { combineLatest, of } from 'rxjs';
import { filter, first, map, switchMap } from 'rxjs/operators';
import { LabelValueUpper } from 'src/app/models/keyvalue';
import { ConfigurationService } from 'src/app/services/configuration/configuration.service';

import { NgxsDataRepository } from '@angular-ru/ngxs/repositories';
import { IConvertMemberRequest, MemberRequestModel, MemberRequestResponse } from '../../models/member';
import { Origin, Reason, Tenant } from '../../models/tenant';
import { UserService } from '../user/user.service';
import { TenantEndpoint } from './tenant-endpoint.service';
import { TenantOrigins, TenantReasons, TenantStateModel } from './tenant.state.models';

// @Persistence()
@StateRepository()
@State<TenantStateModel>({
    name: 'tenantState',
    defaults: {
        allTenants: []
    }
})
@Injectable()
export class TenantService extends NgxsDataRepository<TenantStateModel> {
    name: 'United Healthcare';

    @Selector()
    static activeTenants(state: TenantStateModel) {
        return state.allTenants.filter(tenant => tenant.isActive);
    }

    @Selector()
    static allTenants(state: TenantStateModel) {
        return state.allTenants;
    }

    @Selector()
    static entryPointOrigins(state: TenantStateModel) {
        return state.origins;
    }

    @SelectorOptions({ injectContainerState: false })
    @Selector([TenantService.entryPointOrigins])
    static origins(origins: TenantOrigins) {
        return origins && origins.values;
    }


    @Selector()
    static entryPointReasons(state: TenantStateModel) {
        return state.reasons;
    }

    @SelectorOptions({ injectContainerState: false })
    @Selector([TenantService.entryPointReasons])
    static reasons(reasons: TenantReasons) {
        return reasons && reasons.values;
    }

    get activeTenants$() {
        return this.store.select<Tenant[]>(TenantService.activeTenants).pipe(filter(t => t.length > 0), first());
    }
    get allTenants$() {
        return this.store.select<Tenant[]>(TenantService.allTenants).pipe(filter(t => t.length > 0), first());
    }

    get activeTenantsSync() {
        return this.store.selectSnapshot<Tenant[]>(TenantService.activeTenants);
    }

    get allTenantsSync() {
        return this.store.selectSnapshot<Tenant[]>(TenantService.allTenants);
    }

    constructor(
        private store: Store,
        private tenantEndpoint: TenantEndpoint,
    ) {
        super();
    }


    async ngxsAfterBootstrap() {
        super.ngxsAfterBootstrap();

        this.ctx.setState(produce(this.ctx.getState(), (draft: TenantStateModel) => {
            draft.origins = this.initialState.origins;
            draft.reasons = this.initialState.reasons;
            draft.allTenants = this.initialState.allTenants;
            draft.dispositions = this.initialState.dispositions;
        }));

        await this.initializeGlobalTenantsLitener();
    }

    async initializeGlobalTenantsLitener() {
        await combineLatest([this.store.select<string>(UserService.userIdentifier), this.store.select<boolean>(ConfigurationService.initializedSelector)]).pipe(filter(([userIdentifier, configInitialized]) => !!userIdentifier && configInitialized), first()).toPromise();
        await this.getAllTenants();
    }

    public async findTenantById(id: string): Promise<Tenant> {
        return await this.activeTenants$.pipe(switchMap(tenants => of(tenants.find(tenant => tenant.tenantName === id)))).toPromise();
    }

    public findTenantByIdSync(id: string): Tenant {
        return this.activeTenantsSync?.find(tenant => tenant.tenantName.toLocaleLowerCase() === id.toLocaleLowerCase());
    }

    public async addMember(tenantId: string, request: MemberRequestModel): Promise<MemberRequestResponse> {
        return this.tenantEndpoint.addMemberEndpoint<MemberRequestResponse>(tenantId, request).toPromise();
    }

    public async convertMember(tenantId: string, request: IConvertMemberRequest, targetUrl?: string): Promise<any> {
        return await this.tenantEndpoint.convertMemberEndpoint<any>(tenantId, request, targetUrl).toPromise();
    }


    public async getDirectionType(tenantId: string): Promise<any> {
        return await this.tenantEndpoint.getDirectionTypeEndpoint<string>(tenantId).toPromise();
    }

    async getConfiguration(tenantId: string): Promise<any> {
        return await this.tenantEndpoint.getConfiguration(tenantId);
    }

    async saveConfiguration(tenantId: string, data: any) {
        return await this.tenantEndpoint.saveConfiguration(tenantId, data);
    }

    @DataAction()
    async getAllTenants() {
        const tenants = await this.tenantEndpoint.getAllTenants().pipe(first()).toPromise();
        this.ctx.setState(produce(this.ctx.getState(), (draft: TenantStateModel) => {
            draft.allTenants = tenants;
        }));

    }

    @DataAction()
    async getOriginType(@Payload('tenantId') tenantId: string): Promise<Origin[]> {
        const current = this.ctx.getState();

        if (current.origins && current.origins.tenantId === tenantId && current.origins.values) {
            return current.origins.values;
        }

        const values = !tenantId ? [] : await this.tenantEndpoint.getOriginTypeEndpoint<{ origins: Origin[] }>(tenantId).pipe(map(wrapper => wrapper.origins)).toPromise();


        this.ctx.setState(produce(this.ctx.getState(), (draft: TenantStateModel) => {
            draft.origins = {
                tenantId,
                values
            };
        }));

        return values;
    }

    @DataAction()
    public async getReasonType(@Payload('tenantId') tenantId: string): Promise<Reason[]> {

        const current = this.ctx.getState();

        if (current.reasons && current.reasons.tenantId === tenantId && current.reasons.values) {
            return current.reasons.values;
        }

        const values = !tenantId ? [] : await this.tenantEndpoint.getReasonTypeEndpoint<{ reasons: Reason[] }>(tenantId).pipe(map(wrapper => wrapper.reasons)).toPromise();

        this.ctx.setState(produce(this.ctx.getState(), (draft: TenantStateModel) => {
            draft.reasons = {
                tenantId,
                values
            };
        }));

        return values;
    }

    @DataAction()
    public async getDispositionType(@Payload('tenantId') tenantId: string): Promise<LabelValueUpper[]> {

        const current = this.ctx.getState();
        if (current.dispositions && current.dispositions.tenantId === tenantId && current.dispositions.values) {
            return current.dispositions.values;
        }

        const values = !tenantId ? { reasons: [] } : await this.tenantEndpoint.getDispositionEndpoint<{ reasons: LabelValueUpper[] }>(tenantId).toPromise();

        this.ctx.setState(produce(this.ctx.getState(), (draft: TenantStateModel) => {
            draft.dispositions = {
                tenantId,
                values: values.reasons
            };
        }));

        return values.reasons;
    }
}
